import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import T from '@wojtekmaj/react-t';
import { endOfDay, startOfDay } from 'date-fns';

import colors from '@rewardopl/styles/colors';

import Dialog from '@rewardopl/react-ui/dialog';

import { CodeWrapper, Text } from './your_code_dialog.styles';

import Code from '../../common/code';
import DailyLimitReached from '../../common/daily_limit_reached';
import EmailVerificationRoadblock from '../../common/email_verification_roadblock';

import {
  currentUserState,
  transactionsQuery,
  vendorCardState,
  vendorCardSubscriptionState,
} from '../../../store';

export default function YourCodeDialog() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useAtomValue(currentUserState);
  const cardSubscription = useAtomValue(vendorCardSubscriptionState);

  const card = useAtomValue(vendorCardState);

  const { _id: cardSubscriptionId } = cardSubscription;

  const transactions = useAtomValue(transactionsQuery(cardSubscriptionId));

  const { is_email_verified: isEmailVerified = true } = currentUser;
  const { max_daily_amount: maxDailyAmount, max_daily_transactions: maxDailyTransactions } = card;

  const closeDialog = useCallback(() => {
    navigate(
      {
        ...location,
        hash: '',
      },
      { state: location.state },
    );
  }, [location, navigate]);

  const todaysUserTransactions = transactions.filter((transaction) => {
    if (transaction.type !== 'points') {
      return false;
    }

    const transactionDate = new Date(transaction.created_at);

    const now = new Date();
    const startOfToday = startOfDay(now);
    const endOfToday = endOfDay(now);

    return transactionDate >= startOfToday && transactionDate <= endOfToday;
  });

  const todaysUserTransactionsAmount = todaysUserTransactions.reduce(
    (acc, dbTransaction) => acc + dbTransaction.amount,
    0,
  );

  const todaysUserTransactionsCount = todaysUserTransactions.length;

  const isDailyAmountLimitReached =
    maxDailyAmount && todaysUserTransactionsAmount >= maxDailyAmount;
  const isDailyTransactionsLimitReached =
    maxDailyTransactions && todaysUserTransactionsCount >= maxDailyTransactions;
  const isDailyLimitReached = isDailyAmountLimitReached || isDailyTransactionsLimitReached;

  return (
    <Dialog isOpen={location.hash === '#your-code'} onDismiss={closeDialog} title="My card">
      {(() => {
        if (!isEmailVerified) {
          return <EmailVerificationRoadblock showIcon />;
        }

        if (isDailyLimitReached) {
          return <DailyLimitReached />;
        }

        return (
          <>
            <Text>
              <T>
                Show the code every time you visit Salad Story to earn points for your purchases.
              </T>
            </Text>
            <CodeWrapper>
              <Code color={colors.text} id={currentUser._id} />
            </CodeWrapper>
          </>
        );
      })()}
    </Dialog>
  );
}
